import type { PlayerStore } from "./types";
import type { Song } from "../domain/types";

export const selectActiveQueueType = (s: PlayerStore) => s.activeQueueType;
export const selectActiveQueue = (s: PlayerStore) =>
  s.activeQueueType === "playlist" ? s.playlistQueue : s.foryouQueue;
export const selectCurrentItem = (s: PlayerStore) => {
  const queue = selectActiveQueue(s);
  if (!queue || queue.currentIndex === -1) return null;
  return queue.items[queue.currentIndex];
};

export const selectForyouQueue = (s: PlayerStore) => s.foryouQueue;
export const selectPlaylistQueue = (s: PlayerStore) => s.playlistQueue;

// Memoized active song selector (prevents new object creation)
let cachedActiveSongId: string | null = null;
let cachedActiveSongResult: Song | null = null;

export const selectActiveSong = (s: PlayerStore): Song | null => {
  const currentSongId = selectActiveSongId(s);
  const queue = selectActiveQueue(s);

  if (cachedActiveSongId === currentSongId && cachedActiveSongResult) {
    return cachedActiveSongResult;
  }

  cachedActiveSongId = currentSongId;
  cachedActiveSongResult =
    queue?.items.find((item) => item.metadata.id === currentSongId)?.metadata ??
    null;

  return cachedActiveSongResult;
};

export const selectActiveSongId = (s: PlayerStore) =>
  selectCurrentItem(s)?.metadata?.id ?? null;
export const selectActiveQueueItemId = (s: PlayerStore) =>
  selectCurrentItem(s)?.qid ?? null;
// Primitive selector for duration - avoids object reference issues when switching queues
export const selectActiveSongDuration = (s: PlayerStore) =>
  selectCurrentItem(s)?.metadata?.duration ?? 0;

export const selectPlaybackStatus = (s: PlayerStore) => s.status;
export const selectIsPlaying = (s: PlayerStore) =>
  s.status === "PLAYING" || s.status === "BUFFERING" || s.status === "LOADING";
export const selectIsBuffering = (s: PlayerStore) =>
  s.status === "BUFFERING" || s.status === "LOADING";
// selectBufferedPosition removed — buffered now in shared values (progressState.buffered)
export const selectPlaybackLastError = (s: PlayerStore) => s.playbackLastError;

export const selectRepeatMode = (s: PlayerStore) => s.repeatMode;

export const selectShuffleOn = (s: PlayerStore) => {
  const activeQueue = selectActiveQueue(s);
  if (activeQueue?.type === "playlist") {
    return activeQueue.shuffleState.isOn;
  }
  return false;
};

export const selectActiveCollection = (s: PlayerStore) => {
  const activeQueue = selectActiveQueue(s);
  if (activeQueue?.type === "playlist") {
    return activeQueue.collection;
  }
  return null;
};

export const selectActiveCollectionType = (s: PlayerStore) => {
  if (s.activeQueueType === "foryou") return "foryou";
  if (s.activeQueueType === "playlist" && s.playlistQueue) {
    return s.playlistQueue.collection.type;
  }
  return null;
};

export const selectActiveCollectionId = (s: PlayerStore) => {
  if (s.activeQueueType === "foryou") return "foryou";
  if (s.activeQueueType === "playlist" && s.playlistQueue) {
    return s.playlistQueue.collection.id;
  }
  return null;
};

export const selectActiveCollectionName = (s: PlayerStore) => {
  if (s.activeQueueType === "playlist" && s.playlistQueue) {
    return s.playlistQueue.collection.name ?? null;
  }
  return null;
};

export const selectIsLoadingMore = (s: PlayerStore) => s.isLoadingMore;
export const selectIsQueueLoading = (s: PlayerStore) => s.isLoadingMore; // Map for legacy support
export const selectIsQueueEmpty = (s: PlayerStore) => {
  const activeQueue = selectActiveQueue(s);
  return !activeQueue || activeQueue.items.length === 0;
};

export const selectCanSkip = (s: PlayerStore) => s.canSkip;
export const selectCanSkipReason = (s: PlayerStore) => s.canSkipReason;

// PERF: Combined selector for canSkip state - single subscription instead of two
export const selectCanSkipState = (s: PlayerStore) => ({
  canSkip: s.canSkip,
  reason: s.canSkipReason,
});

export const selectIsAutoPlayEnabled = (s: PlayerStore) => s.isAutoPlayEnabled;
export const selectAutoPlayEnabled = selectIsAutoPlayEnabled; // Alias for tests/backwards compat

export const selectTrackChangeCount = (s: PlayerStore) => s.trackChangeCount;

export const selectHasHydrated = (s: PlayerStore) => s._hasHydrated;

export const selectPlaylistSnapshot = (s: PlayerStore) => {
  const queue = s.playlistQueue;
  if (!queue) return null;
  return {
    collection: queue.collection,
    items: queue.items.map((i) => ({
      queueItemId: i.qid,
      songId: i.metadata.id,
    })),
    currentQueueItemId: queue.items[queue.currentIndex]?.qid ?? null,
  };
};

// Memoized seekable selector (used by Progress.tsx)
let cachedAdState: string | null = null;
let cachedSeekableResult = true;

export const selectSeekable = (s: PlayerStore): boolean => {
  if (cachedAdState === s.adPlaybackState) {
    return cachedSeekableResult;
  }
  cachedAdState = s.adPlaybackState;
  cachedSeekableResult = s.adPlaybackState === "idle";
  return cachedSeekableResult;
};

// Export for external use (e.g., testing)
export const clearSelectorCache = () => {
  cachedAdState = null;
  cachedActiveSongId = null;
  cachedActiveSongResult = null;
};
